// Step 4 - which workspace these mailboxes are connected into.
//
// The list is `workspaces_cache`, written when the API key was validated. It is
// the server's allow-list, not a hint: POST /workspace refuses an id that is not
// in it, so the radio list here and the check there read the same source.
//
// On an Instantly run the cache holds exactly ONE workspace (one key = one
// workspace) and it arrives pre-selected - the step is the explicit
// confirmation it is the RIGHT one, because a wrong key on that platform means
// a wrong workspace, silently. A Bison key is workspace-scoped the same way and
// almost always sees one, so it pre-selects on the same rule.
//
// The Next button is the moment the two halves of the customer's PlusVibe
// identity are proved to be the same account. The API key came from the customer
// directly. On PlusVibe the server also proves the same key against the PARTNER
// listing (x-api-key plus our provider id) before it stores the choice, so a key
// whose account has our provider disabled fails here rather than on every
// browser session - PARTNER_CREDS_INVALID is that check failing.

import { useState } from 'react';

import { sqApi, sqErrorMessage } from '../sequencerApi';
import type { SqStepProps } from './shared';
import { StepUserId } from './StepUserId';

export function StepWorkspace(props: SqStepProps): JSX.Element {
  // Smartlead has no workspace list to pick from - step 4 is a single workspace
  // user id instead. The shell switches steps by run.current_step, so the branch
  // that decides WHICH step-4 lives here, on run.sequencer.
  if (props.detail.run.sequencer === 'smartlead') return <StepUserId {...props} />;

  const { runId, detail, reload, goToStep, toast } = props;
  const { run } = detail;
  const workspaces = run.workspaces_cache ?? [];

  const [choice, setChoice] = useState<string | null>(
    run.workspace_id ??
      // One key = one workspace on Instantly, and usually on Bison too:
      // pre-select the single cached entry so the step reads as a confirmation,
      // not a hunt. A Bison key CAN be scoped to more than one, hence the count
      // check. PlusVibe keys routinely see several and pre-picking one of those
      // would be a guess.
      (run.sequencer !== 'plusvibe' && workspaces.length === 1
        ? (workspaces[0]?.id ?? null)
        : null),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    if (choice === null) return;
    setBusy(true);
    setError(null);
    try {
      await sqApi.setWorkspace(runId, choice);
      await reload();
      await goToStep(5);
    } catch (err) {
      // Surfaced verbatim. PARTNER_CREDS_INVALID in particular names what to go
      // and check, and rewording it here would lose that.
      setError(sqErrorMessage(err));
      toast('error', sqErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="sq-step">
      <div className="sq-step-head">
        <h2>Step 4 - Workspace</h2>
        <p>
          {run.sequencer === 'instantly'
            ? 'One Instantly API key belongs to exactly one workspace - confirm this is the RIGHT one before anything is connected into it.'
            : run.sequencer === 'bison'
              ? 'The workspace this Bison API key is scoped to. Usually exactly one - confirm and continue.'
              : "The workspace these mailboxes are connected into. This is the list the customer's API key can see; picking one also proves the PlusVibe login off the sheet belongs to the same account."}
        </p>
      </div>
      <div className="sq-step-body">
        {workspaces.length === 0 ? (
          <div className="sq-notice sq-notice--warn">
            No workspaces are cached for this run. Go back to step 2 and validate the{' '}
            {run.sequencer === 'instantly'
              ? 'Instantly'
              : run.sequencer === 'bison'
                ? 'Bison'
                : 'PlusVibe'}{' '}
            API key - the list comes back with it.
          </div>
        ) : (
          <div className="sq-radios">
            {workspaces.map((w) => (
              <label
                key={w.id}
                className={`sq-radio${choice === w.id ? ' sq-radio--on' : ''}`}
              >
                <input
                  type="radio"
                  name="sq-workspace"
                  value={w.id}
                  checked={choice === w.id}
                  onChange={() => setChoice(w.id)}
                  disabled={busy}
                />
                <span className="sq-radio-text">
                  <span className="sq-radio-label">{w.name}</span>
                  <span className="sq-radio-blurb sq-mono">{w.id}</span>
                </span>
              </label>
            ))}
          </div>
        )}

        {error && <div className="sq-notice sq-notice--err">{error}</div>}

        <div className="sq-actions">
          <button
            type="button"
            className="sq-btn sq-btn--primary"
            onClick={() => void save()}
            disabled={busy || choice === null}
          >
            {busy ? 'Checking...' : 'Next'}
          </button>
        </div>
      </div>
    </section>
  );
}
